import * as React from 'react';
import { useState } from 'react';
import { toast } from 'react-toastify';

import { AccountAndContactInfo } from './AccountAndContactInfo';
import { CustomerStats } from './CustomerStats';
import { CustomerTreatments } from './CustomerTreatments';
import { axios } from '../../api';
import { isAdmin, isOpsAdmin, isAllModuleAccessUser, isTeamMember } from '../../pages/constants';
import { Customer, Treatment } from '../../pages/types';
import { BOTTOM_TOAST, generateErrorMsg, generateFullName, getCountryFlag } from '../../utils';
import { ConfirmationModal } from '../ConfirmationModal';
import { Button } from '../buttons/Button';

interface CustomerDetailProps {
  customer: Customer;
  treatments?: Treatment[];
}

export const CustomerDetail = ({ customer, treatments }: CustomerDetailProps) => {
  const showImpersonate = isOpsAdmin() || isAdmin() || isAllModuleAccessUser() || isTeamMember();
  // TODO: Ungate when one-click login is ready to launch
  const showOneClickLogin = isAllModuleAccessUser();
  const [showOneClickLoginConfirmationModal, setShowOneClickLoginConfirmationModal] =
    useState(false);

  const handleImpersonateClick = () => {
    axios
      .get(`impersonate/?account_id=${customer.id}`)
      .then((response) => {
        window.open(`${response.data.redirect_to}`, '_blank');
      })
      .catch((e) => {
        toast.error(generateErrorMsg(e), BOTTOM_TOAST);
      });
  };

  const handleOneClickLogin = () => {
    axios
      .post('one_click_login_request', { email: customer.email })
      .then(() => {
        toast.success(
          `Success! ${customer.email} will receive an email with a login link within a couple of minutes.`,
          BOTTOM_TOAST
        );
      })
      .catch((e) => {
        toast.error(generateErrorMsg(e), BOTTOM_TOAST);
      })
      .finally(() => setShowOneClickLoginConfirmationModal(false));
  };

  return (
    <div className="py-3 flex flex-col justify-center align-center gap-4">
      <div className="flex justify-between align-top">
        <h1>
          <b>Customer:</b>{' '}
          {`${getCountryFlag(customer.default_country)} ${generateFullName(
            customer.first_name,
            customer.last_name
          )} (${customer.email})`}
        </h1>
        <div className="flex flex-row justify-center align-top gap-4">
          {showOneClickLogin && (
            <Button
              label="Send Login Link"
              variant="secondary"
              onClick={() => setShowOneClickLoginConfirmationModal(true)}
            />
          )}
          {showImpersonate && (
            <Button label="Login as Customer" variant="primary" onClick={handleImpersonateClick} />
          )}
        </div>
      </div>
      <div className="flex flex-row flex-wrap gap-4">
        <div className="w-[35%]">
          <AccountAndContactInfo customer={customer} />
        </div>{' '}
        <div className="w-[30%]">
          <CustomerStats customer={customer} />
        </div>
        <div className="w-[32%]">
          <CustomerTreatments treatments={treatments} />
        </div>
      </div>
      {showOneClickLoginConfirmationModal && (
        <ConfirmationModal
          title="Are you sure you want to send this customer a one-click login link?"
          customMessage={
            <p className="text-center mb-4 text-sm">
              We'll email:
              <br />
              <span className="font-bold">{customer.email}</span>
              <br />a secure link that logs them directly into their account.
            </p>
          }
          confirmLabel="Send Login Link"
          onConfirm={handleOneClickLogin}
          onCancel={() => setShowOneClickLoginConfirmationModal(false)}
        />
      )}
    </div>
  );
};
